import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Guideline §11 — Status Labels.
 * One definition per semantic state so a status reads identically in a table
 * cell, a card header and beside an entity name.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'draft';

export type StatusKind =
  | 'active'
  | 'approved'
  | 'reviewed'
  | 'current'
  | 'valid'
  | 'signed'
  | 'pending'
  | 'waiting-review'
  | 'expiring'
  | 'expired'
  | 'rejected'
  | 'draft'
  | 'inactive'
  | 'signature-required'
  | 'not-signed-off'
  | 'critical'
  | 'moderate'
  | 'low'
  | 'not-supplied';

const TONES: Record<StatusTone, string> = {
  success: 'bg-signal-50 text-signal-700 ring-signal-200',
  warning: 'bg-gold-50 text-gold-700 ring-gold-200',
  danger: 'bg-danger-50 text-danger-600 ring-danger-200',
  info: 'bg-digital-50 text-digital-600 ring-digital-200',
  neutral: 'bg-cloud text-muted ring-line',
  draft: 'bg-pale-blue/70 text-muted ring-line',
};

/** Maps a known status to its tone and display label. */
const KINDS: Record<StatusKind, { tone: StatusTone; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  approved: { tone: 'success', label: 'Approved' },
  reviewed: { tone: 'success', label: 'Reviewed' },
  current: { tone: 'success', label: 'Current' },
  valid: { tone: 'success', label: 'Valid' },
  signed: { tone: 'success', label: 'Signed' },
  pending: { tone: 'warning', label: 'Pending Review' },
  'waiting-review': { tone: 'warning', label: 'Waiting review' },
  expiring: { tone: 'warning', label: 'Expiring Soon' },
  moderate: { tone: 'warning', label: 'Moderate' },
  expired: { tone: 'danger', label: 'Expired' },
  rejected: { tone: 'danger', label: 'Rejected' },
  critical: { tone: 'danger', label: 'Critical' },
  'signature-required': { tone: 'info', label: 'Signature Required' },
  low: { tone: 'info', label: 'Low' },
  draft: { tone: 'draft', label: 'Draft' },
  inactive: { tone: 'neutral', label: 'Inactive' },
  'not-signed-off': { tone: 'neutral', label: 'Not Signed Off' },
  'not-supplied': { tone: 'neutral', label: 'Not supplied' },
};

export interface StatusPillProps {
  /** A known status. Supplies both tone and label unless overridden. */
  status?: StatusKind;
  /** Override the tone, or set it directly when no `status` is given. */
  tone?: StatusTone;
  /** Override the label, or set it directly when no `status` is given. */
  children?: ReactNode;
  /** Leading dot. Used where the pill sits alone and needs extra affordance. */
  dot?: boolean;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

export function StatusPill({
  status,
  tone,
  children,
  dot = false,
  icon,
  size = 'sm',
  className,
}: StatusPillProps) {
  const preset = status ? KINDS[status] : undefined;
  const resolvedTone = tone ?? preset?.tone ?? 'neutral';
  const label = children ?? preset?.label ?? '—';

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full font-semibold ring-1 ring-inset',
        size === 'sm' ? 'px-2.5 py-0.5 text-xs' : 'px-3 py-1 text-[13px]',
        TONES[resolvedTone],
        className,
      )}
    >
      {dot && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
      {icon && <span aria-hidden className="shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>}
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Neutral outlined chip for non-status metadata — assigned projects, tags,
 * employment type. Deliberately distinct from StatusPill so a reader never
 * mistakes a project name for a compliance state.
 */
export function Chip({
  children,
  tone = 'outline',
  className,
}: {
  children: ReactNode;
  tone?: 'outline' | 'solid' | 'accent';
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center rounded px-2 py-1 text-xs font-medium',
        tone === 'outline' && 'border border-line bg-white text-body',
        tone === 'solid' && 'bg-cloud text-muted',
        tone === 'accent' && 'bg-signal-50 text-signal-700',
        className,
      )}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}
